const env = require('bench-environment').default;

const _ = require('lodash');

class ClientInfoCtrl {
  constructor(
    $q,
    $window,
    Address,
    Client,
    ClientInfo,
    ClientSettings,
    CountryCollection,
    BillingPartnerCollection,
    BookkeeperCollection,
    blConfirmService,
    FeatureMeta,
    ManagerCollection,
    RegionCollection,
    SalesPersonCollection,
    User,
    NotificationsService,
    Person,
  ) {
    const { clientId } = this;
    // Models & Collections
    const client = new Client(clientId);
    const { inboxState } = client;
    this.disableFields = inboxState !== 'TESTING';
    let address = {}; // to be set when client is updated
    const settings = new ClientSettings(clientId);
    const featureMeta = new FeatureMeta();
    featureMeta.$fetch();
    const info = new ClientInfo(clientId);
    this.info = info;
    this.countries = new CountryCollection();
    this.managers = new ManagerCollection();
    this.bookkeepers = new BookkeeperCollection();
    this.salespeople = new SalesPersonCollection();
    this.regions = new RegionCollection();
    this.billingPartners = new BillingPartnerCollection();
    this.principalPerson = new Person(client.principalPerson.id);

    this.isSuperUser = User.isSuperUser();
    this.isBankingSupportUser = User.isBankingSupportUser();

    // sections
    this.team = {};
    this.team.client = client.$viewModel();
    this.team.save = () => client.$put(this.team.client.$data());

    this.business = {};
    this.business.client = client.$viewModel();
    this.business.info = info.$viewModel();
    this.business.save = () => {
      const infoPromise = info.$put(this.business.info.$data());
      const clientPromise = client.$put(this.business.client.$data());
      return $q.all([clientPromise, infoPromise]);
    };

    this.general = {};
    this.general.client = client.$viewModel();
    this.general.info = info.$viewModel();
    this.general.countryChanged = () => {
      const openConfirmMessage = `Are you sure? This will change \
  client's country and province and also erase existing \
  address information. <strong>Also, you can only change \
  country if client doesn't have any transactions or \
  Bank / CC ledgers.</strong>`;
      blConfirmService.openConfirm(openConfirmMessage).then(
        () => (this.general.address.countryId = this.general.countryId),
        () => (this.general.countryId = this.general.address.countryId),
      );

      if (this.general.address.countryId !== address.countryId) {
        return (this.general.address.province = null); // force select of new province
      }
    };
    this.general.address = {};
    client.$onSet(() => {
      address = new Address(client.address.businessAddressId);
      this.general.address = address.$viewModel();

      return address.$promise.then(() => {
        this.general.countryId = address.countryId;
        return (this.general.address.country = address.countryId);
      });
    });
    this.general.save = () => {
      const clientPromise = client.$put(this.general.client.$data());
      const infoPromise = this.info.$put(this.general.info.$data());
      const addressPromise = () => {
        if (this.general.address.countryId !== address.countryId) {
          const promise = client.changeCountry(this.getCountryCode(this.general.address.countryId));
          return promise.then(() => address.$put(this.general.address.$data()));
        }
        return address.$put(this.general.address.$data());
      };

      return $q.all([clientPromise, infoPromise, addressPromise()]);
    };

    this.principalPerson
      .$getEnrolledMultifactorDevices()
      .then(response => (this.principalPerson.mfaDevices = response.data));

    this.enrollMfaDevice = () => {
      NotificationsService.loader({
        resolve: this.principalPerson.$enrollMultifactorDevice(),
        loadingText: 'Sending enrollment email to primary contact...',
        successText: 'Enrollment email sent.',
        failureText: 'An enrollment email could not be sent.',
      });
    };

    this.clearMfaDevices = () => {
      NotificationsService.loader({
        resolve: this.principalPerson.$clearAllMultifactorDevices(),
        loadingText: 'Clearing all primary contact mfa devices...',
        successText: 'Devices cleared for primary contact.',
        failureText: 'Devices could not be cleared for primary contact.',
      }).then(() => {
        this.principalPerson
          .$getEnrolledMultifactorDevices()
          .then(response => (this.principalPerson.mfaDevices = response.data));
      });
    };

    this.status = {};
    this.status.client = client.$viewModel();
    this.status.info = info.$viewModel();
    this.status.save = () => {
      const clientPromise = client.$put(this.status.client.$data());
      const infoPromise = this.info.$put(this.status.info.$data());
      return $q.all([clientPromise, infoPromise]);
    };

    /**
     * This is a workaround to bypass the need for superuser permissions to update
     * inbox state, which is the case for the normal client API endpoint
     */
    this.status.startTrial = () => {
      const newInboxState = 'TRIAL_EXECUTE';
      client.setInboxState(newInboxState).then(() => client.$fetch()); // re-sync VM
    };

    this.billing = {};
    this.billing.client = client.$viewModel();
    // The Billing app lives under the dashboard host at /billing; its contract list accepts a
    // ?clientId= deep link.
    this.billing.openBillingUi = () => {
      $window.open(`${env.location.dashboard}/billing/contracts?clientId=${client.id}`);
    };

    this.billing.save = () => client.$put(this.billing.client.$data());
    this.billingPartners.$fetch().then(() => {
      if (!this.billing.client.more.partnerId) {
        this.billing.client.more.partnerId = this.billingPartners.findIdByName(
          this.billing.client.more.partner,
        );
      }
    });

    this.billingPartners.save = () => {
      const billingPartnerName = this.billingPartners.findNameById(
        this.billing.client.more.partnerId,
      );

      this.billing.client.more.partner = billingPartnerName || null;

      client.$put(this.billing.client.$data());
    };

    this.features = {};
    this.features.meta = featureMeta.$viewModel();
    this.features.settings = settings.$viewModel();
    this.features.save = () => settings.$put(this.features.settings.$data());

    // misc
    this.getCountryCode = countryId => {
      if (countryId && this.countries.$lastUpdated > 0) {
        return this.countries.$find(country => country.id === countryId.toString()).code;
      }
    };

    this.salesForceSearchLink = () => {
      const b64search = btoa(
        `{"componentDef":"forceSearch:searchPageDesktop","attributes":{"term":"${clientId}"}}`,
      );
      return `https://benchaccounting.lightning.force.com/one/one.app#${b64search}`;
    };

    this.confirmFeatureToggle = (featureId, value) => {
      if (value === false) return;
      // When enabled -> undo the feature toggle in the UI and then ask for confirmation
      this.features.settings.features[featureId] = !value;

      const openConfirmMessage =
        `<b>Are you sure you want to enable this option?</b></br>` +
        `It should only be used if your integration is causing major performance issues in the Bench App - typically when handling 4,000 to 5,000+ transactions per month.</br></br>` +
        `Once enabled, manually set the initial sync date and then reset the connection.`;
      blConfirmService.openConfirm(openConfirmMessage).then(() => {
        this.features.settings.features[featureId] = value;
      });
    };

    this.hasConsolidateFeatures = () => {
      return (
        this.features.meta &&
        Object.values(this.features.meta).some(f => f?.id?.startsWith('consolidate'))
      );
    };

    this.activeConversation = null;

    this.conversationSelect = id => (this.activeConversationId = id);

    this.filingTypes = [
      'Sole prop',
      'Partnership',
      'S-Corp',
      'S-Corp (DE Franchise)',
      'C-Corp/Corporation',
      'C-Corp/Corporation (DE Franchise)',
      'Not for Profit',
    ];

    this.structures = [
      'Sole prop',
      'Partnership',
      'S-Corp',
      'S-Corp (DE Franchise)',
      'C-Corp/Corporation',
      'C-Corp/Corporation (DE Franchise)',
      'Not for Profit',
    ];

    this.inboxStates = [
      { name: 'Prospective', value: 'PROSPECTIVE' }, // legacy / not used
      { name: 'Tr - Setup', value: 'TRIAL_QUALIFY' }, // default state
      { name: 'Tr - Working', value: 'TRIAL_EXECUTE' }, // set in this view
      { name: 'Tr - Call', value: 'TRIAL_REVIEW' }, // set by onboarding specialist in SF
      { name: 'Tr - Convert', value: 'TRIAL_CONVERT' }, // set by onboarding specialist in SF
      { name: 'Lost Trial', value: 'LOST_TRIAL' }, // legacy state for handling lost trials
      { name: 'Catch Up', value: 'CATCH_UP' }, // set in this view
      { name: 'Keep up', value: 'KEEP_UP' }, // set in this view
      { name: 'Churn', value: 'CHURN' }, // client cancels but account remains
      { name: 'Demo', value: 'DEMO' }, // eng internal
      { name: 'Testing', value: 'TESTING' }, // eng internal
      { name: 'Waitlist', value: 'WAITLIST' }, // legacy state for handling capacity issues
    ];

    this.clientCareStates = [
      { name: 'Healthy', value: 'HEALTHY' },
      { name: 'Unresponsive', value: 'UNRESPONSIVE' },
      { name: 'Delinquent', value: 'DELINQUENT' },
      { name: 'On Hold', value: 'ON_HOLD' },
    ];
  }

  get allowedInboxStates() {
    const currentState = this.status.client.inboxState;

    switch (currentState) {
      case 'TRIAL_EXECUTE': // intentional fallthrough
      case 'TRIAL_CONVERT': // intentional fallthrough
      case 'CATCH_UP': // intentional fallthrough
      case 'KEEP_UP':
        // CATCH_UP and KEEP_UP only acceptable alternatives to current state
        return _.filter(
          this.inboxStates,
          state =>
            state.value === 'CATCH_UP' || state.value === 'KEEP_UP' || state.value === currentState,
        );

      default:
        // In other cases, only allow the current state as selection
        // (ie, no changes are allowed)
        return _.filter(this.inboxStates, state => state.value === currentState);
    }
  }

  get isInboxStateReadyForTrial() {
    return this.status.client.inboxState === 'TRIAL_QUALIFY';
  }
}

module.exports = ClientInfoCtrl;
